import { readCoverageBetaTiming } from "@/components/coverage-beta/CoverageBetaActivityTracker";
import { useCoverageBetaNextTaskPrefetch } from "@/hooks/useCoverageBetaNextTaskPrefetch";
import {
  isEmptyAreaSubmittable,
  useCoverageBetaStore,
} from "@/store/useCoverageBetaStore";
import { buildCoverageBetaResult } from "@/utils/build-coverage-beta-result";
import { isTaskAlreadyFinalized } from "@/utils/task-already-finalized";
import {
  COVERAGE_QA_REJECT_REASONS,
  CompleteCoverageQaEditPayloadSchema,
  type CompleteCoverageQaEditPayload,
  type CoverageQaRejectReason,
} from "@/utils/zod-schemas/complete-coverage-qa-edit-payload-schema";
import { mvpApiCompleteJobTask } from "@labeling-tools/api/lebron/generated";
import { mvpApiGetNextPendingJobTaskOptions } from "@labeling-tools/api/lebron/generated/@tanstack/react-query.gen";
import { JobTaskDetailSchema } from "@labeling-tools/api/lebron/generated/types.gen";
import { useFetchJob } from "@labeling-tools/api/lebron/react-query-hooks";
import {
  Button,
  Checkbox,
  Menu,
  Popover,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { IconChevronDown, IconMessage } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { values } from "lodash";
import { useState } from "react";
import { useParams } from "react-router-dom";

interface Props {
  taskDetailResp: JobTaskDetailSchema | undefined;
}

/**
 * Header actions for an editable, whole-review Coverage QA task: leave the
 * labeler feedback, reject the task, or submit the corrected result. Submit
 * sends the same coverage result a labeler's submission does — lebron
 * publishes OOS events from it and diffs it against the labeler's.
 */
export const CoverageQaEditActions = (props: Props) => {
  const { taskDetailResp } = props;
  const params = useParams<{ jobId: string }>();
  const job_id = params?.jobId ?? "";
  const task_id = taskDetailResp?.id ?? "";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const { data: job } = useFetchJob(job_id);

  const qaEditNote = useCoverageBetaStore((s) => s.qaEditNote);
  const setQaEditNote = useCoverageBetaStore((s) => s.setQaEditNote);
  const qaEditFlagLabeler = useCoverageBetaStore((s) => s.qaEditFlagLabeler);
  const setQaEditFlagLabeler = useCoverageBetaStore(
    (s) => s.setQaEditFlagLabeler,
  );
  const isHydrated = useCoverageBetaStore((s) => s.qaEditOriginal != null);
  // Same bar as a labeler's submit: every kept area decided.
  const allReviewed = useCoverageBetaStore((s) =>
    values(s.idxToFeEmptyArea)
      .filter((area) => !area.isDeleted)
      .every(isEmptyAreaSubmittable),
  );

  const { nextTaskId, nextTaskDetail, isPrefetchReady } =
    useCoverageBetaNextTaskPrefetch({
      jobId: job_id,
      currentTaskId: task_id,
      enabled: !!job_id && !!task_id,
    });

  const complete = async (
    buildResult: () => CompleteCoverageQaEditPayload["result"],
  ) => {
    // Live check, not the render-time one: between tasks the store resets a
    // frame before this re-renders, so a fast second click could otherwise
    // submit the next task's empty state.
    if (!task_id || !useCoverageBetaStore.getState().qaEditOriginal) return;
    setIsSubmitting(true);
    try {
      const parsed = CompleteCoverageQaEditPayloadSchema.safeParse({
        result: buildResult(),
      });
      if (!parsed.success) {
        throw new Error("CompleteCoverageQaEditPayloadSchema parsing error");
      }
      const { error } = await mvpApiCompleteJobTask({
        body: parsed.data,
        path: { job_id, task_id },
      });
      if (error && !isTaskAlreadyFinalized(error)) throw error;

      const nextPendingKey = mvpApiGetNextPendingJobTaskOptions({
        path: { job_id },
      }).queryKey;
      queryClient.invalidateQueries({ queryKey: ["job-tasks", job_id] });
      if (isPrefetchReady && nextTaskId && nextTaskDetail) {
        queryClient.setQueryData(nextPendingKey, nextTaskDetail);
        queryClient.invalidateQueries({
          queryKey: nextPendingKey,
          exact: true,
        });
      } else {
        await queryClient.invalidateQueries({
          queryKey: nextPendingKey,
          exact: true,
        });
      }
    } catch (error) {
      console.error("error: ", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const qaNote = () => qaEditNote.trim() || undefined;

  const handleSubmit = () =>
    complete(() => {
      const state = useCoverageBetaStore.getState();
      const result = buildCoverageBetaResult(
        state.idxToFeEmptyArea,
        state.persons,
        {
          basePlanogramIds: state.basePlanogramIds,
          overridePlanogramIds: state.overridePlanogramIds,
          extraPlanogramIds: state.extraPlanogramIds,
          boxIdxToSlot: state.boxIdxToSlot,
        },
        {
          testing: job?.testing ?? false,
          primaryImageId: state.primaryImageId,
        },
      );
      return {
        ...result,
        metadata: { ...result.metadata, ...(readCoverageBetaTiming() ?? {}) },
        status: "reviewed",
        qa_note: qaNote(),
        flag_labeler: state.qaEditFlagLabeler,
      };
    });

  const handleReject = (reason: CoverageQaRejectReason) =>
    complete(() => ({ status: "rejected", reason, qa_note: qaNote() }));

  let disabledReason = "";
  if (!task_id || !isHydrated) {
    disabledReason = "Loading the submission under review…";
  } else if (!allReviewed) {
    disabledReason = "Finish every box before submitting";
  }

  return (
    <>
      <Popover width={300} position="bottom-end" withArrow shadow="md">
        <Popover.Target>
          <Button
            variant="subtle"
            size="sm"
            leftSection={<IconMessage size={16} />}
            color={qaEditNote || qaEditFlagLabeler ? "blue" : "gray"}
          >
            Feedback
          </Button>
        </Popover.Target>
        <Popover.Dropdown>
          <Stack gap="xs">
            <Text size="xs" c="dimmed">
              Optional — shared with the labeler.
            </Text>
            <Textarea
              placeholder="What did the labeler miss?"
              autosize
              minRows={3}
              value={qaEditNote}
              onChange={(e) => setQaEditNote(e.currentTarget.value)}
            />
            <Checkbox
              label="Flag labeler for follow-up"
              checked={qaEditFlagLabeler}
              onChange={(e) => setQaEditFlagLabeler(e.currentTarget.checked)}
            />
          </Stack>
        </Popover.Dropdown>
      </Popover>

      <Menu position="bottom-end" withArrow>
        <Menu.Target>
          <Button
            variant="outline"
            color="red"
            size="sm"
            disabled={!task_id || !isHydrated || isSubmitting}
            rightSection={<IconChevronDown size={14} />}
          >
            Reject
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>Nothing will be published</Menu.Label>
          {COVERAGE_QA_REJECT_REASONS.map(({ value, label }) => (
            <Menu.Item key={value} onClick={() => handleReject(value)}>
              {label}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>

      <Button
        size="sm"
        onClick={handleSubmit}
        loading={isSubmitting}
        disabled={!!disabledReason}
        title={disabledReason || undefined}
      >
        Submit QA
      </Button>
    </>
  );
};
